import React from "react"
import type { FC } from "react"
import { Stack } from "@chakra-ui/react"

import DelegatesInfoCard from "governance/components/DelegatesInfoCard"
import GovernanceLineChart from "governance/components/GovernanceLineChart"
import GovernanceDelegatesExplore from "governance/components/GovernanceDelegatesExplore"
import type {
  Organization,
  Governor,
  GovernanceTopAdvocatesQuery,
  GovernanceTopAdvocatesQueryVariables,
} from "query/graphql"
import { useDevice } from "common/hooks/useDevice"
import { DelegatesSortBy, GovernanceTopAdvocatesDocument } from "query/graphql"
import { useQuery } from "query/hooks/useQuery"
import { GrpcErrorType } from "common/helpers/fetcher"
import GenericModal from "ui/components/GenericModal"
import GovernanceDelegatesSummary from "governance/components/GovernanceDelegatesSummary"
import type { OrganizationAnnouncement } from "organization/helpers/fetcher"
import { spacing } from "theme/foundations/spacing"
import { isMultiChainTokenIds } from "organization/helpers/organization"

type Props = {
  organization: Organization
  governorSlug: string
  governors: Governor[]
  isWhiteLabel: boolean
  isMobileModalOpen: boolean
  organizationAnnouncement?: OrganizationAnnouncement
  setIsMobileModalOpen: (isOpen: boolean) => void
}
const GovernanceDelegatesLayout: FC<Props> = ({
  organization,
  governorSlug,
  governors,
  isWhiteLabel,
  isMobileModalOpen,
  organizationAnnouncement,
  setIsMobileModalOpen,
}) => {
  const [governor] = governors

  const { onLargeDevice, onLittleDevice } = useDevice()

  const { data: section, isLoading: isLoadingSection } = useQuery<
    GovernanceTopAdvocatesQuery,
    GovernanceTopAdvocatesQueryVariables
  >(
    {
      query: GovernanceTopAdvocatesDocument,
      variables: {
        input: {
          id: governor?.id ?? "",
        },
        delegatesInput: {
          filters: { governorId: governor?.id ?? "" },
          sort: { isDescending: true, sortBy: DelegatesSortBy.Votes },
          page: { limit: 5 },
        },
      },
      omittedErrors: [GrpcErrorType.NotFound, GrpcErrorType.Internal],
    },
    { enabled: Boolean(governor) },
  )

  const hasToken = organization?.tokenIds?.length > 0

  // The query required in the Top Delegates chart doesn't work on multiple different tokens
  // since it needs to calculate voteChanges and it requires a governor
  const isMultiChain = isMultiChainTokenIds(organization?.tokenIds)

  return (
    <>
      <Stack
        direction="row"
        display={onLargeDevice}
        spacing={spacing.containerGap}
      >
        <Stack direction="column" width="70%">
          {organization ? (
            <GovernanceDelegatesExplore
              governorSlug={governorSlug}
              governors={governors}
              isWhiteLabel={isWhiteLabel}
              karmaName={
                organization.metadata?.karmaName
                  ? organization.metadata?.karmaName
                  : undefined
              }
              organization={organization}
            />
          ) : null}
        </Stack>

        <Stack
          alignSelf="flex-start"
          spacing={spacing.containerGap}
          width="30%"
        >
          <DelegatesInfoCard
            organizationAnnouncement={organizationAnnouncement}
          />
          {!isLoadingSection && section && !isMultiChain ? (
            <GovernanceLineChart section={section} />
          ) : null}

          {hasToken ? (
            <GovernanceDelegatesSummary
              governorId={governor?.id}
              governorIds={governors.map((governor) => governor.id)}
              organization={organization}
            />
          ) : null}
        </Stack>
      </Stack>

      <Stack direction="column" display={onLittleDevice}>
        {organization ? (
          <GovernanceDelegatesExplore
            governorSlug={governorSlug}
            governors={governors}
            isWhiteLabel={isWhiteLabel}
            karmaName={
              organization.metadata?.karmaName
                ? organization.metadata?.karmaName
                : undefined
            }
            organization={organization}
          />
        ) : null}

        <GenericModal
          isOpen={isMobileModalOpen}
          title="Delegates info"
          onClose={() => {
            setIsMobileModalOpen(false)
          }}
        >
          <Stack spacing={spacing.containerGap}>
            <DelegatesInfoCard />
            {!isLoadingSection && section && !isMultiChain ? (
              <GovernanceLineChart section={section} />
            ) : null}

            {hasToken ? (
              <GovernanceDelegatesSummary
                governorId={governor?.id}
                governorIds={governors.map((governor) => governor.id)}
                organization={organization}
              />
            ) : null}
          </Stack>
        </GenericModal>
      </Stack>
    </>
  )
}

export default GovernanceDelegatesLayout
